import React, { useCallback, useMemo, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { loading } from '../../actions/Loading';
import api from '../../api/api';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { CampaignStatsResult } from '../../model/CampaignStatsResult';
import VirtualizedStatsTable from './VirtualizedStatsTable';
import { FaExclamation, FaEye, FaLink } from 'react-icons/fa';
import CampaignStatsSummaryComponent from './CampaignStatsSummaryComponent';
import { IGlobalState } from '../../model/IGlobalState';
import * as Roles from '../../model/Roles';
import { GetFailedBatchSendEventsResult } from '../../model/GetFailedBatchSendEventsResult';
import AnimatedSaveButton from '../AnimatedSaveButton';
import AlertEx from '../AlertEx';
import { CampaignStats } from '../../model/CampaignStats';
import { fromNow } from '../../util/util';

interface IProps{
    combinedTicker:string
    match?: { params: {  campaignId:string }};
}

const Wrapper = styled.div`

.status {
    color:#fff;
    padding:2px 7px;
    border-radius: 5px;
    font-weight: 700;
    display: inline-flex;      /* puts icon + text in a row */
  align-items: center;       /* vertical centering */
  gap: 4px;                  
    
}
.status .icon {
  font-size: 1em;            /* makes icon same size as text */
}
.unopened{
    background-color: #F42525;
}
.unclicked {
    background-color: #F49D25;
}
.delivered {
    background-color: #22C33C;
}
.status-other{
    background-color: #F49D25;
}
.refresh-button-container {
    margin-top: 1rem;
    display: flex;
    flex-direction: row-reverse;
    gap: 10px;
}
.refresh-button-container .saveButton {
    padding: 4px 10px;
    font-size: 0.85rem;
}
.campaign-stats-header{
margin: 1rem 0;
}
.title {

    font-weight: 700;
    font-size: 1.2rem;
}
.link-button {
    background: none;
    border: none;
    padding: 0;
    color: #0d6efd;
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.85rem;
}
.stats-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}
.stats-filter input {
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.85rem;
    width: 320px;
    max-width: 100%;
}
.stats-filter__count {
    font-size: 0.8rem;
    color: #666;
}
`;

const ColumnEmailAddress = 'Email Address'
const ColumnOpened = 'Opened'
const ColumnClicked = 'Clicked'
const ColumnStatus = 'Status'

const FormatTime = (val:any) => {
    return <span>
                    <span>{fromNow(val)}</span>
                    
                </span>
}

const capitalizeFirst = (str:any)=> {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const headers = [ ColumnEmailAddress, ColumnOpened, ColumnClicked, ColumnStatus ];

const FailedEventDetails: React.FC<{ event: any }> = ({ event }) => {
    const [ show, setShow ] = useState(false);
    return <div>
        <div>{event.message}</div>
        <button type="button" className="link-button" onClick={() => setShow(s => !s)}>
            {show ? 'Hide details' : 'Show details'}
        </button>
        { show && <pre>{JSON.stringify(event, null, 2)}</pre> }
    </div>
}

const statsCellFormatter = (columnName:string, cell:any, stat:CampaignStats):React.ReactNode => {

    if(columnName ===ColumnEmailAddress ){
        return <span title={cell} data-referenceid={stat.referenceId} data-toencrypted={stat.toEncrypted} data-nameencrypted={stat.nameEncrypted}>
                 {cell}
                </span>
    }
    else if(columnName ===ColumnOpened ){
        if(cell){
            return FormatTime(cell)
        }else{
            return <span className="status unopened"> <FaEye className="icon"/> Unopened   </span>
        }
    }
    else if(columnName ===ColumnClicked){
        if(cell){
            return FormatTime(cell)
        }else{
            return <span className="status unclicked"> <FaLink className="icon"/> Unclicked</span>
        }
    }
    else if(columnName ===ColumnStatus){
        const cssStatusClass = cell === 'delivered' ? 'delivered' : 'status-other';
        return <span> 
                <span className={`status ${cssStatusClass}`}>
                {capitalizeFirst(cell)}
                </span>
            { stat.errorMessage && <span title={stat.errorMessage}> <FaExclamation className="icon"/> </span>}
            </span>
    }
    return null;
}

const getName = (stat:CampaignStats):string => {
    let name = stat.to||'';
    if(stat.name){
        name += ` - ${stat.name}`;
    }
    if(!name){
        console.warn('CampaignStats has no name', stat)
    }
    return name;
}

const CampaignStatsComponent: React.FC<IProps>= (props:IProps) => {
    const dispatch = useDispatch();
    const [ item, setItem ] = useState<CampaignStatsResult>();
    const [ failedBatchSendEvents, setFailedBatchSendEvents ] = useState<GetFailedBatchSendEventsResult>();
    const [ query, setQuery ] = useState('');
    const [ html, setHtml ] = useState<string>();
    const user = useSelector((state: IGlobalState) => state.user);
    const isAdmin = user.roles.hasOwnProperty(Roles.Admin);
    const campaignId = decodeURIComponent(props.match!.params.campaignId);

    useAsyncEffect(async (isMounted:()=>boolean)=> {
        
        refresh(isMounted);
        
      }, []);

      const refresh = async (isMounted:()=>boolean) =>{
            dispatch(loading(true));
        const result = await api().getCampaignStats(campaignId);

        if (isAdmin){
            const failedBatchSendEvents = await api().getFailedBatchSendEvents(campaignId);
            setFailedBatchSendEvents(failedBatchSendEvents);
        }

        if(isMounted()){
            setItem(result)   
        }
        dispatch(loading(false));
        return true;
      }


    const resendFailed = async (id: string) => {
        if (window.confirm('Resend Failed?')) {
            let result = await api().resendFailedBatchSendEvent(id);
            if (result.success) {
                setFailedBatchSendEvents(prev => prev && {
                    ...prev,
                    events: prev.events.map(e => e.id === id ? { ...e, resentSuccess: true } : e)
                });
                AlertEx('Sent!');
            }
            else {
                AlertEx(result.message);                
            }
        }
        return true;

    }

    const viewHtml = async () => {
        if (html !== undefined) {
            setHtml(undefined);
            return true;
        }
        const campaign = await api().getCampaign(campaignId);
        setHtml(campaign.html || '');
        return true;
    }

    const data = useMemo<any[][]>(
        () => item ? item.stats.map(stat => [getName(stat), stat.opened || '', stat.clicked || '', stat.status, stat]) : [],
        [item]
    );

    const cellFormatter = useCallback(
        (columnName:string, cell:any, stat:CampaignStats):React.ReactNode => statsCellFormatter(columnName, cell, stat),
        []
    );

    // client-side filter on the already-loaded rows; r[0] is "email - name" so it matches both.
    // Needed because virtualization keeps only the visible rows in the DOM, so native Ctrl+F can't find off-screen emails.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? data.filter(r => String(r[0]).toLowerCase().includes(q)) : data;
    }, [data, query]);


    return <div>

{ item && <Wrapper>
   <div className='campaign-stats-header'>
    <div>
         <span className='title'>Subscriber Activity - </span>
    <span>
{item.subject}
    </span>
    </div>
   
    <div >Created: {fromNow(item.created)}</div>
   </div>
  
    { item.stats.length === 0 && <p className='warning'>Stats being generated...please refresh in a moment</p> }

    
    

    { isAdmin && failedBatchSendEvents && failedBatchSendEvents.events.length > 0 && <table>
        <thead>
            <tr><th>Actions</th><th>Details</th></tr>
        </thead>
        <tbody>
            
            { failedBatchSendEvents.events.map((x,i)=> <tr key={i}>
                <td>{ x.resentSuccess
                    ? <AnimatedSaveButton onClick={()=>Promise.resolve(true)} startingText="Resent successfully" disabled={true} />
                    : <AnimatedSaveButton onClick={()=>resendFailed(x.id)} startingText="Resend" progressText="Wait..." completedText="Sent" /> }</td>
                <td><FailedEventDetails event={x} /></td>
                
            </tr>)}
        </tbody>

    </table> }

   <CampaignStatsSummaryComponent summary={item.summary}/>

        <div className='refresh-button-container'>
            <AnimatedSaveButton onClick={()=>refresh(()=>true)} startingText="Refresh" progressText="Wait..." completedText="Refreshed" />
            <AnimatedSaveButton onClick={viewHtml} startingText={html !== undefined ? 'Hide Campaign' : 'View Campaign'} progressText="Wait..." completedText={html !== undefined ? 'Hide Campaign' : 'View Campaign'} />
        </div>

        { html !== undefined && <iframe
            srcDoc={html}
            style={{ width: '100%', height: '800px', border: '1px solid #ccc', marginTop: '1rem' }}
            title="Campaign HTML"
            sandbox="allow-same-origin"
        /> }

        
<p><small>Note: If a subscriber has not opened, but clicked on the email, it's possible that the user blocks the tracking pixel from loading, which is why we don't see the opened time.</small></p>

        <div className='stats-filter'>
            <input
                type="text"
                placeholder="Filter by email or name..."
                value={query}
                onChange={e => setQuery(e.target.value)}
            />
            <span className='stats-filter__count'>Showing {filtered.length.toLocaleString()} of {data.length.toLocaleString()}</span>
        </div>

        <VirtualizedStatsTable headers={headers} data={filtered} cellFormatter={cellFormatter}  />

</Wrapper>}

    </div>
}

export default CampaignStatsComponent